import { Input, Row, Col, message, Form } from 'antd';
import { Button } from '../../../../../../button';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useSelected } from '../../../../../../../providers/selectedContext';
import { useAppContext } from '../../../../../../../providers/AppContext';
import { addZoneToCorrelation } from '../../../../../../../api/data';

type FormValues = {
  zones: {
    start: string | number;
    mean: string | number;
    end: string | number;
    color: string;
  }[];
};

interface ZoneInputsProps {
  setIsModalOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;
}

const ZoneInputs = (props: ZoneInputsProps) => {
  const { selectedInfo, setSelectedInfo } = useSelected();
  const { upsertProject } = useAppContext();

  const { control, handleSubmit, setValue, watch, getValues } = useForm<FormValues>({
    defaultValues: {
      zones: [{ start: '', mean: '', end: '', color: '#1677ff' }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'zones'
  });

  const onSubmit = async (data: FormValues) => {
    if (!selectedInfo.project) return;
    props.setLoading(true);

    const payload = {
      project_uid: selectedInfo.project.uid,
      zones: data.zones.map(zone => {
        const start = parseFloat(String(zone.start).replace(',', '.'));
        const end = parseFloat(String(zone.end).replace(',', '.'));
        const mean = parseFloat(String(zone.mean).replace(',', '.'));
        return {
          color: zone.color,
          start: isNaN(start) ? 0 : start,
          end: isNaN(end) ? 0 : end,
          mean: isNaN(mean) ? 0 : mean,
        };
      })
    };

    try {
      const response = await addZoneToCorrelation(
        selectedInfo.project.uid,
        payload
      );
      setSelectedInfo({ project: response });
      upsertProject(response);
      message.success('Rock Types added successfully');
      props.setIsModalOpen(false);
    } catch (error) {
      message.error('Error adding zones');
      console.error(error);
    } finally {
      props.setLoading(false);
    }
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    fieldIndex: number
  ) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const pastedRows = pastedText.split(/\r?\n/).filter(row => row.trim() !== '');

    pastedRows.forEach((row, rowIndex) => {
      const currentIndex = fieldIndex + rowIndex;
      const columns = row.split('\t');

      const startValue = columns[0] ? columns[0].trim().replace(',', '.') : '';
      const meanValue = columns[1] ? columns[1].trim().replace(',', '.') : '';
      const endValue = columns[2] ? columns[2].trim().replace(',', '.') : '';

      if (currentIndex < fields.length) {
        setValue(`zones.${currentIndex}.start`, startValue);
        setValue(`zones.${currentIndex}.mean`, meanValue);
        if (endValue) {
          setValue(`zones.${currentIndex}.end`, endValue);
        }
      } else {
        append({
          start: startValue,
          mean: meanValue,
          end: endValue,
          color: '#1677ff'
        });
      }
    });
  };

  const watchedZones = watch('zones');
  useEffect(() => {
    watchedZones.forEach((zone, index) => {
      const meanIsEmpty = zone.mean === '' || zone.mean == null;

      if (meanIsEmpty) {
        const start = parseFloat(String(zone.start).replace(',', '.'));
        const end = parseFloat(String(zone.end).replace(',', '.'));

        if (!isNaN(start) && !isNaN(end)) {
          const calculatedMean = ((start + end) / 2).toFixed(2);
          if (String(getValues(`zones.${index}.mean`)) !== calculatedMean) {
            setValue(`zones.${index}.mean`, calculatedMean, { shouldValidate: true });
          }
        }
      }
    });
  }, [watchedZones, setValue, getValues]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div style={{ marginTop: '2rem' }}>
        {fields.map((field, index) => (
          <Row
            gutter={8}
            key={field.id}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Col span={4}>
              <p style={{ margin: 0, textAlign: 'center' }}>Rock type {index + 1}</p>
            </Col>

            <Col span={5}>
              <Controller name={`zones.${index}.start`} {...{ control }} rules={{ required: 'Required' }}
                render={({ field: controllerField, fieldState }) => (
                  <Form.Item validateStatus={fieldState.error ? 'error' : ''} help={fieldState.error?.message} style={{ marginBottom: 16 }}>
                    <Input {...controllerField} onPaste={event => handlePaste(event, index)} placeholder="Min" type="text" inputMode="decimal" />
                  </Form.Item>
                )}
              />
            </Col>

            <Col span={6}>
              <Controller name={`zones.${index}.mean`} {...{ control }} rules={{ required: 'Required' }}
                render={({ field: controllerField, fieldState }) => (
                  <Form.Item validateStatus={fieldState.error ? 'error' : ''} help={fieldState.error?.message} style={{ marginBottom: 16 }}>
                    <Input {...controllerField} placeholder="Mean" type="text" inputMode="decimal" />
                  </Form.Item>
                )}
              />
            </Col>

            <Col span={5}>
              <Controller name={`zones.${index}.end`} {...{ control }} rules={{ required: 'Required' }}
                render={({ field: controllerField, fieldState }) => (
                  <Form.Item validateStatus={fieldState.error ? 'error' : ''} help={fieldState.error?.message} style={{ marginBottom: 16 }}>
                    <Input {...controllerField} placeholder="Max" type="text" inputMode="decimal" />
                  </Form.Item>
                )}
              />
            </Col>

            <Col span={4} style={{ textAlign: 'center' }}>
              <Controller name={`zones.${index}.color`} {...{ control }}
                render={({ field: controllerField }) => (
                  <Form.Item style={{ marginBottom: 16 }}>
                    <Input type="color" {...controllerField} style={{ width: '50px' }} />
                  </Form.Item>
                )}
              />
            </Col>
          </Row>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
          <Button
            variant="primary"
            size="sm"
            startIcon={<PlusOutlined />}
            onClick={() => append({ start: '', mean: '', end: '', color: '#1677ff' })}
          />
          <Button
            variant="ghost"
            size="sm"
            style={{ marginLeft: '0.5rem' }}
            startIcon={<MinusOutlined />}
            disabled={fields.length <= 1}
            onClick={() => fields.length > 1 && remove(fields.length - 1)}
          />

          <Button
            variant="primary"
            type="submit"
            style={{ marginLeft: 'auto' }}
          >
            Submit Rock Types
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ZoneInputs;
